import { useReducer } from 'react';
import { useTranslation } from 'react-i18next';

import { analytics } from '@/analytics';
import { ModalComponent } from '@/components/molecules/ModalComponent';
import { ToastSuccess } from '@/components/molecules/ToastTemplate/ToastTemplate';
import { defaultOperationDate } from '@/components/organisms/FishGroupForms';
import { usePermissions } from '@/contexts/auth';
import { useOperateFishGroup } from '@/domain/fishGroups';

import {
    buildOperationRequest,
    createInitialState,
    operateWizardReducer,
} from './operateWizardReducer';
import { renderStep } from './wizard/renderStep';
import { StepIndicator } from './wizard/StepIndicator';
import { useOperateWizardData } from './wizard/useOperateWizardData';
import { WizardFooter } from './wizard/WizardFooter';

import styles from './operateModals.module.scss';

interface InitialFishGroup {
    id: number;
    name: string;
    currentCageId: number;
    locationId: number;
    locationName: string;
}

interface Props {
    clientId: number;
    initialFishGroup: InitialFishGroup | null;
    isOpen: boolean;
    onClose: () => void;
}

/**
 * Multi-mode wizard for composition-changing operations.
 *
 * The operator picks an explicit mode (split / merge / split_and_merge /
 * redistribution) on Step 0; the wizard then walks them through mode-specific
 * steps. State lives here in a `useReducer` shared between body and footer
 * via ModalComponent's slots; the wizard re-mounts every time a different
 * fish group opens the modal thanks to the `key={initialFishGroup.id}` on
 * the inner component.
 */
export default function OperateFishGroupModal(props: Props) {
    if (!props.initialFishGroup) return null;
    return (
        <OperateFishGroupModalInner
            key={props.initialFishGroup.id}
            clientId={props.clientId}
            initialFishGroup={props.initialFishGroup}
            isOpen={props.isOpen}
            onClose={props.onClose}
        />
    );
}

interface InnerProps {
    clientId: number;
    initialFishGroup: InitialFishGroup;
    isOpen: boolean;
    onClose: () => void;
}

function OperateFishGroupModalInner(props: InnerProps) {
    const { clientId, initialFishGroup, isOpen, onClose } = props;
    const { t } = useTranslation('fishGroups');
    const { canMoveBioscopes } = usePermissions();

    const [state, dispatch] = useReducer(operateWizardReducer, undefined, () =>
        createInitialState(
            {
                fishGroupId: initialFishGroup.id,
                name: initialFishGroup.name,
                currentCageId: initialFishGroup.currentCageId,
            },
            initialFishGroup.locationId,
            defaultOperationDate().toISOString()
        )
    );

    const {
        isLoading,
        hasError,
        locationCages,
        validationErrors,
        cageWarnings,
        operationMinDate,
        populationById,
    } = useOperateWizardData(clientId, state);

    const initialSource = state.sources.find((s) => s.fishGroupId === state.initialSourceId) ?? {
        fishGroupId: initialFishGroup.id,
        name: initialFishGroup.name,
        currentCageId: initialFishGroup.currentCageId,
    };

    const {
        operate,
        isLoading: isSubmitting,
        error: submitError,
        reset: resetMutation,
    } = useOperateFishGroup(clientId, {
        onSuccess: () => {
            analytics.featureActionUsed({ featureName: 'fish_group_management' });
            ToastSuccess(t('actions.operate.confirm.successToast'), 'fish-group-operate-success');
            resetMutation();
            handleClose();
        },
    });

    /**
     * Close the wizard and wipe its in-flight state. Used by Cancel, the
     * header X, and the success path — all entry points to "exit the
     * wizard" should leave it ready for a fresh run, so reopening the
     * modal for the same fish group never resumes a half-filled flow.
     */
    const handleClose = () => {
        dispatch({ type: 'reset', operationDate: defaultOperationDate().toISOString() });
        onClose();
    };

    const handleSubmit = async () => {
        await operate(buildOperationRequest(state, { notBefore: operationMinDate })).catch(() => {
            // Error stays on `submitError` via the hook; the ConfirmStep
            // surfaces it inline. We swallow here so the promise rejection
            // doesn't bubble to React's error boundary.
        });
    };

    // Bioscope companion step only applies when the operator has permission
    // and at least one source cage carries a bioscope. The flag lives on the
    // modal so StepIndicator + WizardFooter agree with renderStep about
    // which steps are visible right now.
    const hasBioscopePermission = canMoveBioscopes(clientId, state.locationId);
    const sourceCageIdsForBioscopes = new Set(
        state.sources.map((s) => s.currentCageId).filter((id): id is number => id != null)
    );
    const anySourceHasBioscope = locationCages.some(
        (c) => sourceCageIdsForBioscopes.has(c.cageId) && c.activeBioscopes.length > 0
    );
    const includeBioscopeStep = hasBioscopePermission && anySourceHasBioscope;

    const body = (
        <div>
            <p className={styles.contextLine}>
                {t('actions.operate.subtitle', { location: initialFishGroup.locationName })}
            </p>
            <StepIndicator state={state} includeBioscopeStep={includeBioscopeStep} />
            {renderStep({
                state,
                dispatch,
                isLoading,
                hasError,
                locationCages,
                initialSource,
                validationErrors,
                cageWarnings,
                submitError,
                populationById,
                operationMinDate,
                t,
            })}
        </div>
    );

    const footer = (
        <WizardFooter
            state={state}
            dispatch={dispatch}
            onClose={handleClose}
            validationErrors={validationErrors}
            cageWarnings={cageWarnings}
            isSubmitting={isSubmitting}
            onSubmit={handleSubmit}
            includeBioscopeStep={includeBioscopeStep}
        />
    );

    // The wizard covers four different actions, so a fixed title would either
    // be wrong or too vague once one is chosen. Stay generic on the chooser
    // step, then name the chosen action — reusing the labels the chooser cards
    // already render so the two can never drift.
    const title = state.operationKind
        ? t('actions.operate.titleWithAction', {
              action: t(`actions.operate.kind.cards.${state.operationKind}.title`),
              name: initialFishGroup.name,
          })
        : t('actions.operate.title', { name: initialFishGroup.name });

    return (
        <ModalComponent
            isOpen={isOpen}
            onClose={handleClose}
            backdrop="static"
            size="xl"
            title={title}
            body={body}
            footer={footer}
        />
    );
}
